/**
 * Patient-related data fetchers
 */
import { QueryClient, MutationOptions, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { updatePatientStatus, createOrUpdatePatient } from "@/src/actions/patient";
import { toast } from "@/src/hooks/use-toast";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import {
  CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY,
  TASKS_CACHE_KEY,
  TASK_CACHE_KEY,
} from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Patient, PatientStatus, PatientType } from "@/src/models/patient";
import { PatientMetadata } from "@/src/models/patient-metadata";
import {
  getPatientById,
  getPatientsByIds,
  getPatientsMetadataByIds,
  getPaginatedPatientsMetadata,
  searchPatientsByQuery,
  type PaginatedPatientsMetadataResponse,
} from "@/src/requests/patient";
import { isNotFound } from "@/src/requests/utils/fetch-json";

export const PATIENTS_CACHE_KEY = "patients";
export const LEADS_CACHE_KEY = "leads";
export const PAGINATED_PATIENTS_METADATA_CACHE_KEY = "paginated-patients-metadata";
export const PATIENTS_METADATA_BY_IDS_CACHE_KEY = "patients-metadata-by-ids";
export const PATIENTS_SEARCH_CACHE_KEY = "patients-search";

const deDuplicatePatients = (patients: Patient[]) => {
  // First sort patients to prioritize those with more complete information
  const sortedPatients = [...patients].sort((a, b) => {
    // Prioritize patients with more complete information
    const aComplete = !!(a.givenName && a.familyName && a.dateOfBirth);
    const bComplete = !!(b.givenName && b.familyName && b.dateOfBirth);

    if (aComplete && !bComplete) return -1;

    if (!aComplete && bComplete) return 1;

    return 0;
  });

  // Then deduplicate based on first name, last name, and DOB
  const uniquePatients = [];
  const patientKeys = new Set();

  for (const patient of sortedPatients) {
    const givenName = (patient.givenName || "").toLowerCase();
    const familyName = (patient.familyName || "").toLowerCase();
    const dob = patient.dateOfBirth || "";
    const key = `${givenName}|${familyName}|${dob}`;

    if (!patientKeys.has(key)) {
      patientKeys.add(key);

      uniquePatients.push(patient);
    }
  }

  return uniquePatients;
};

export function useLeadByDisplayId(displayId: string) {
  const queryClient = useQueryClient();

  const {
    data: lead,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [LEADS_CACHE_KEY, displayId],
    queryFn: async () => {
      const leadsCache = queryClient.getQueryData<Patient[]>([LEADS_CACHE_KEY]);

      if (leadsCache) {
        const cachedLead = leadsCache.find((lead) => lead.displayId === displayId);

        if (cachedLead) {
          return cachedLead;
        }
      }

      try {
        const lead = await getPatientById(displayId);

        if (lead) {
          updateLeadsQueryData(queryClient, lead);
        }

        return lead;
      } catch (err) {
        if (isNotFound(err as Error)) {
          return null;
        }

        throw err;
      }
    },
    staleTime: Infinity,
    placeholderData: (previousData, _query) => {
      // First check if this specific lead query has cached data
      const cachedLeadQuery = queryClient.getQueryData<Patient>([LEADS_CACHE_KEY, displayId]);

      if (cachedLeadQuery) {
        return cachedLeadQuery;
      }

      // Then check the main leads cache
      const leadsCache = queryClient.getQueryData<Patient[]>([LEADS_CACHE_KEY]);

      if (leadsCache) {
        const cachedLead = leadsCache.find((lead) => lead.displayId === displayId);

        if (cachedLead) {
          return cachedLead;
        }
      }

      // Keep previous data visible while loading to prevent flicker
      return previousData;
    },
  });

  return { lead, isFetching, isLoading, isError };
}

export function usePatientById(id: string) {
  const queryClient = useQueryClient();

  const patientsCache = queryClient.getQueryData<Patient[]>([PATIENTS_CACHE_KEY]);
  const cachedPatient = (() => {
    if (!patientsCache || !id) return undefined;

    return patientsCache.find((patient) => patient.id === id);
  })();

  const {
    data: fetchedPatient,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [PATIENTS_CACHE_KEY, "byId", id],
    queryFn: async () => {
      try {
        const patient = await getPatientById(id);

        if (patient) {
          updatePatientsQueryData(queryClient, patient);
        }

        return patient;
      } catch (err) {
        if (isNotFound(err as Error)) {
          return null;
        }

        throw err;
      }
    },
    enabled: !!id && !cachedPatient,
    staleTime: Infinity,
    placeholderData: cachedPatient,
  });

  const patient = cachedPatient ?? fetchedPatient ?? null;

  return { patient, isFetching, isLoading, isError };
}

export function usePatientByDisplayId(displayId: string) {
  const queryClient = useQueryClient();

  const patientsCache = queryClient.getQueryData<Patient[]>([PATIENTS_CACHE_KEY]);
  const cachedPatient = (() => {
    if (!patientsCache || !displayId) return undefined;

    return patientsCache.find((patient) => patient.displayId === displayId);
  })();

  const {
    data: fetchedPatient,
    isFetching,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [PATIENTS_CACHE_KEY, displayId],
    queryFn: async () => {
      try {
        const patient = await getPatientById(displayId);

        if (patient) {
          updatePatientsQueryData(queryClient, patient);
        }

        return patient;
      } catch (err) {
        if (isNotFound(err as Error)) {
          return null;
        }

        throw err;
      }
    },
    enabled: !!displayId && !cachedPatient,
    staleTime: Infinity,
    placeholderData: (previousData) => {
      if (cachedPatient) {
        return cachedPatient;
      }

      return previousData;
    },
  });

  const patient = cachedPatient ?? fetchedPatient ?? null;

  return { patient, isFetching, isLoading, isError };
}

export function usePatientsByIds(ids: Set<string>) {
  const queryClient = useQueryClient();
  const uniqueIds = [...ids].sort();
  const sortedIdsKey = uniqueIds.join(",");

  // Subscribe to the main cache to get updates when it changes
  const { data: patientsCache } = useQuery<Patient[]>({
    queryKey: [PATIENTS_CACHE_KEY],
    queryFn: () => Promise.resolve([]),
    enabled: false,
    staleTime: Infinity,
  });

  const patientsMap = (() => {
    if (!patientsCache) return new Map<string, Patient>();

    const map = new Map<string, Patient>();

    for (const patient of patientsCache) {
      if (patient.id) {
        map.set(patient.id, patient);
      }
    }

    return map;
  })();

  const missingIds = (() => {
    return uniqueIds.filter((id) => !patientsMap.has(id));
  })();

  const { isFetching, isLoading, isError } = useQuery({
    queryKey: [PATIENTS_CACHE_KEY, "byIds", sortedIdsKey],
    queryFn: async () => {
      if (missingIds.length === 0) {
        return [];
      }

      const fetched = await getPatientsByIds(new Set(missingIds));

      // Update cache with fetched patients
      for (const patient of fetched) {
        updatePatientsQueryData(queryClient, patient);
      }

      return fetched;
    },
    enabled: missingIds.length > 0,
    staleTime: Infinity,
  });

  // Use cache as single source of truth to avoid duplicates
  const patients = (() => {
    return uniqueIds.map((id) => patientsMap.get(id)).filter((p): p is Patient => p !== undefined);
  })();

  return {
    patients,
    isFetching,
    isLoading,
    isError,
  };
}

/**
 * Light-weight companion to `usePatientsByIds` that returns only the
 * `PatientMetadata` fields (name, displayId, phones, …) for the given ids.
 * Use this when the caller just needs to render a label / avatar / link and
 * doesn't need the full nested `Patient` model — e.g. the comms inbox
 * sidebar listing one row per conversation partner.
 */
export function usePatientsMetadataByIds(ids: Set<string>) {
  const uniqueIds = [...ids].sort();
  const sortedIdsKey = uniqueIds.join(",");

  const { data, isFetching, isLoading, isError } = useQuery({
    queryKey: [PATIENTS_METADATA_BY_IDS_CACHE_KEY, sortedIdsKey],
    queryFn: () => getPatientsMetadataByIds(new Set(uniqueIds)),
    enabled: uniqueIds.length > 0,
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const patients = data ?? [];

  return {
    patients,
    isFetching,
    isLoading,
    isError,
  };
}

/**
 * Server-side patient search backed by the `patients_metadata` view's trigram
 * index. Returns full `Patient[]` for matched rows so callers can use the
 * results directly in the EntityMatcher (which copies fields off of the
 * selected patient into the active block).
 */
export function usePatientSearch(
  query: string,
  options: { enabled?: boolean; limit?: number; type?: PatientType } = {}
) {
  const { enabled = true, limit = 20, type } = options;
  const trimmed = query.trim();

  const { data, isFetching, isLoading, isError, error } = useQuery({
    queryKey: [PATIENTS_SEARCH_CACHE_KEY, trimmed, limit, type ?? null],
    queryFn: () => searchPatientsByQuery(trimmed, { limit, type }),
    enabled: enabled && trimmed.length >= 2,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  return {
    patients: data ?? [],
    isFetching,
    isLoading,
    isError,
    error,
  };
}

export const updatePatientsQueryData = (queryClient: QueryClient, updatedPatient: Patient) => {
  queryClient.setQueryData([PATIENTS_CACHE_KEY], (oldPatients: Patient[] = []) => {
    const exists = oldPatients.some((p) => p.id === updatedPatient.id);

    if (!exists) {
      return [...oldPatients, updatedPatient];
    }

    return oldPatients.map((p) => (p.id === updatedPatient.id ? updatedPatient : p));
  });

  queryClient.setQueryData(["patient", updatedPatient.displayId], updatedPatient);
  queryClient.setQueryData([PATIENTS_CACHE_KEY, updatedPatient.displayId], updatedPatient);

  queryClient.setQueryData(["patient-by-id", updatedPatient.id], updatedPatient);
};

export const updateLeadsQueryData = (queryClient: QueryClient, updatedLead: Patient) => {
  queryClient.setQueryData([LEADS_CACHE_KEY], (oldLeads: Patient[] = []) => {
    const exists = oldLeads.some((l) => l.id === updatedLead.id);

    if (!exists) {
      return [...oldLeads, updatedLead];
    }

    return oldLeads.map((l) => (l.id === updatedLead.id ? updatedLead : l));
  });

  queryClient.setQueryData(["lead", updatedLead.displayId], updatedLead);

  queryClient.setQueryData(["lead-by-id", updatedLead.id], updatedLead);
};

export const mutatePatientOptions = (
  patientId: string | null | undefined,
  queryClient: QueryClient
): MutationOptions<void, Error, { patientId: string; newStatus: PatientStatus }, { previousPatients: Patient[] }> => {
  if (!patientId) {
    return {
      mutationFn: () => Promise.resolve(),
    };
  }

  return {
    mutationFn: async ({ patientId, newStatus }: { patientId: string; newStatus: PatientStatus }) => {
      return await updatePatientStatus(patientId, newStatus);
    },
    onMutate: async ({ patientId, newStatus }) => {
      await queryClient.cancelQueries({ queryKey: [PATIENTS_CACHE_KEY] });
      const previousPatients = queryClient.getQueryData<Patient[]>([PATIENTS_CACHE_KEY]) ?? [];

      queryClient.setQueryData([PATIENTS_CACHE_KEY], (patients: Patient[] = []) =>
        patients.map((p) => (p.id === patientId ? { ...p, status: newStatus } : p))
      );

      return { previousPatients };
    },
    onError: (error, variables, context) => {
      logger.error("Error updating patient");

      if (context) {
        queryClient.setQueryData([PATIENTS_CACHE_KEY], context.previousPatients);
      }

      toast({
        title: "Something went wrong updating the patient. Please refresh the page and try again.",
        description: error.message,
        variant: "destructive",
      });
    },
  };
};

/** A save the server rejected with a message meant for staff (e.g. an EHR identifier conflict). */
class PatientSaveRejectedError extends Error {}

export function useCreateOrUpdatePatientMutation(overwriteWithNull: boolean = true) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patient: Patient) => {
      const result = await createOrUpdatePatient(patient, overwriteWithNull);

      if (result.error !== undefined) throw new PatientSaveRejectedError(result.error);

      return result.patient;
    },
    onMutate: async (patient) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [PATIENTS_CACHE_KEY] });
      const isLead = patient.type === PatientType.LEAD;

      if (isLead) {
        await queryClient.cancelQueries({ queryKey: [LEADS_CACHE_KEY] });
      }

      // Snapshot the previous values
      const previousPatients = queryClient.getQueryData<Patient[]>([PATIENTS_CACHE_KEY]) ?? [];
      const previousLeads = isLead ? (queryClient.getQueryData<Patient[]>([LEADS_CACHE_KEY]) ?? []) : [];
      const previousPatient = patient.displayId
        ? queryClient.getQueryData<Patient>([PATIENTS_CACHE_KEY, patient.displayId])
        : undefined;
      const previousLead =
        isLead && patient.displayId
          ? queryClient.getQueryData<Patient>([LEADS_CACHE_KEY, patient.displayId])
          : undefined;

      // Optimistically update the patients and individual patient queries
      queryClient.setQueryData([PATIENTS_CACHE_KEY], (oldPatients: Patient[] = []) => {
        const patientExists = oldPatients.some((p) => p.id === patient.id);

        if (patientExists) {
          // Update existing patient
          return oldPatients.map((p) => (p.id === patient.id ? patient : p));
        } else {
          // Add new patient
          return [patient, ...oldPatients];
        }
      });

      // Also update leads cache if this is a lead
      if (isLead) {
        queryClient.setQueryData([LEADS_CACHE_KEY], (oldLeads: Patient[] = []) => {
          const leadExists = oldLeads.some((l) => l.id === patient.id);

          if (leadExists) {
            // Update existing lead
            return oldLeads.map((l) => (l.id === patient.id ? patient : l));
          } else {
            // Add new lead
            return [patient, ...oldLeads];
          }
        });
      }

      if (patient.displayId) {
        queryClient.setQueryData([PATIENTS_CACHE_KEY, patient.displayId], patient);

        if (isLead) {
          queryClient.setQueryData([LEADS_CACHE_KEY, patient.displayId], patient);
        }
      }

      // Return a context object with the snapshotted values
      return { previousPatients, previousLeads, previousPatient, previousLead, isLead };
    },
    onSuccess: (updatedPatient) => {
      updatePatientsQueryData(queryClient, updatedPatient);

      // Also update leads cache and individual lead queries if this is a lead
      if (updatedPatient.type === PatientType.LEAD) {
        updateLeadsQueryData(queryClient, updatedPatient);
      }

      queryClient.invalidateQueries({ queryKey: [CAN_RESUME_AWAITING_INFORMATION_CACHE_KEY] });
    },
    onError: (error, patient, context) => {
      logger.error("Error creating/updating patient:");

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context) {
        queryClient.setQueryData([PATIENTS_CACHE_KEY], context.previousPatients);

        // Also rollback individual patient queries if they were optimistically updated
        if (patient.displayId) {
          const previousPatient =
            context.previousPatient ?? context.previousPatients.find((p) => p.displayId === patient.displayId);

          if (previousPatient) {
            queryClient.setQueryData([PATIENTS_CACHE_KEY, patient.displayId], previousPatient);
          } else {
            queryClient.removeQueries({ queryKey: [PATIENTS_CACHE_KEY, patient.displayId], exact: true });
          }
        }

        // Rollback leads cache if this was a lead
        if (context.isLead && context.previousLeads) {
          queryClient.setQueryData([LEADS_CACHE_KEY], context.previousLeads);

          if (patient.displayId) {
            const previousLead =
              context.previousLead ?? context.previousLeads.find((l) => l.displayId === patient.displayId);

            if (previousLead) {
              queryClient.setQueryData([LEADS_CACHE_KEY, patient.displayId], previousLead);
            } else {
              queryClient.removeQueries({ queryKey: [LEADS_CACHE_KEY, patient.displayId], exact: true });
            }
          }
        }
      }

      toast({
        title: "Failed to save patient",
        description:
          error instanceof PatientSaveRejectedError
            ? error.message
            : "There was an error saving the patient. Please try again.",
        variant: "destructive",
      });
    },
    meta: {
      invalidates: [[TASKS_CACHE_KEY], [TASK_CACHE_KEY], [REFERRALS_CACHE_KEY]],
    },
  });
}

/**
 * Custom hook for paginated patient metadata fetching using the patients_metadata view
 */
export function usePaginatedPatientsMetadata({
  columnFilters,
  page = 0,
  pageSize = 30,
  sortBy,
  sortOrder,
  query,
  enabled = true,
}: {
  columnFilters?: {
    status?: string[];
    type?: PatientType[];
  };
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  query?: string;
  enabled?: boolean;
} = {}) {
  const queryClient = useQueryClient();
  // When the caller doesn't explicitly pass a `type` filter (i.e. the key is omitted),
  // default to ESTABLISHED. When the caller passes `type: undefined` explicitly, treat
  // it as "no type filter" so leads are also searchable (e.g. global search).
  const typeFilter = columnFilters && "type" in columnFilters ? columnFilters.type : [PatientType.ESTABLISHED];

  const patientsQuery = useQuery({
    queryKey: [
      PAGINATED_PATIENTS_METADATA_CACHE_KEY,
      { ...columnFilters, type: typeFilter },
      page,
      pageSize,
      sortBy,
      sortOrder,
      query,
    ],
    queryFn: () =>
      getPaginatedPatientsMetadata({
        page,
        pageSize,
        status: columnFilters?.status,
        type: typeFilter,
        sortBy,
        sortOrder,
        query,
      }),
    enabled,
    staleTime: 5 * 1000,
    refetchInterval: enabled ? 5 * 1000 : false,
    // Keep previous data while refetching to prevent UI flicker when totalCount temporarily sets to 0
    placeholderData: (previousData) => previousData,
  });

  const paginatedData: PaginatedPatientsMetadataResponse | undefined = patientsQuery.data;
  const patientsMap: Record<string, PatientMetadata> = paginatedData?.patients || {};

  // Prefetch next page (n+1) only if we know it exists
  const hasMore = (!patientsQuery?.isFetching && paginatedData?.hasMore) ?? false;

  if (hasMore) {
    queryClient.prefetchQuery({
      queryKey: [PAGINATED_PATIENTS_METADATA_CACHE_KEY, columnFilters, page + 1, pageSize, sortBy, sortOrder, query],
      queryFn: () =>
        getPaginatedPatientsMetadata({
          page: page + 1,
          pageSize,
          status: columnFilters?.status,
          type: columnFilters?.type,
          sortBy,
          sortOrder,
          query,
        }),
      staleTime: 5 * 1000,
    });
  }

  return {
    patientsMap,
    patients: Object.values(patientsMap),
    totalCount: paginatedData?.totalCount || 0,
    hasMore: paginatedData?.hasMore || false,
    page: paginatedData?.page || page,
    pageSize: paginatedData?.pageSize || pageSize,
    isFetching: patientsQuery.isFetching,
    isLoading: patientsQuery.isLoading,
    isError: patientsQuery.isError,
    refetch: patientsQuery.refetch,
  };
}
